import { HtmlFormattingOptions } from './html-formatting-options';

export class HtmlSanitizer {
	public constructor(private formattingOptions: HtmlFormattingOptions) {}

	public sanitize(input: string): string {
		input = this.normalizeTag(input, 'b', 'strong');
		input = this.normalizeTag(input, 'i', 'em');
		input = this.normalizeTag(input, 'strike', 'del');
		input = this.normalizeTag(input, 'div', 'p');
		input = this.normalizeTag(input, 'h1', 'p');
		input = this.normalizeTag(input, 'h2', 'p');
		input = this.normalizeTag(input, 'h3', 'p');

		input = this.removeEmptyTag(input, 'p');
		input = this.removeEmptyTag(input, 'h4');
		input = this.removeEmptyTag(input, 'h5');
		input = this.removeEmptyTag(input, 'h6');

		input = this.normalizeAttributeValue(input, 'target', '_blank');
		input = this.normalizeImgSource(input);

		input = this.removeForbiddenHtml(input);

		return input;
	}

	private normalizeTag(input: string, tagFrom: string, tagTo: string): string {
		const tagOpenWithSpace = new RegExp(`<${tagFrom}\\s`, 'ig');
		input = input.replace(tagOpenWithSpace, `<${tagTo} `);

		const tagOpenWithoutSpace = new RegExp(`<${tagFrom}>`, 'ig');
		input = input.replace(tagOpenWithoutSpace, `<${tagTo}>`);

		const tagCloseWithSpace = new RegExp(`</${tagFrom}\\s`, 'ig');
		input = input.replace(tagCloseWithSpace, `</${tagTo} `);

		const tagCloseWithoutSpace = new RegExp(`</${tagFrom}>`, 'ig');
		input = input.replace(tagCloseWithoutSpace, `</${tagTo}>`);

		return input;
	}

	private removeEmptyTag(input: string, tag: string): string {
		const expressionA = new RegExp(`<${tag}\\s*>\\s*</${tag}\\s*>`, 'ig');
		input = input.replace(expressionA, '');

		const expressionB = new RegExp(`<${tag}\\s*/>`, 'ig');
		input = input.replace(expressionB, '');

		return input;
	}

	private normalizeAttributeValue(input: string, attribute: string, value: string): string {
		const expressionA = new RegExp(`${attribute}='[A-z0-9_-]*'`, 'ig');
		input = input.replace(expressionA, `${attribute}='${value}'`);

		const expressionB = new RegExp(`${attribute}="[A-z0-9_-]*"`, 'ig');
		input = input.replace(expressionB, `${attribute}="${value}"`);

		return input;
	}

	private normalizeImgSource(input: string): string {
		const wrapperTag = 'a-31f16667-1148-44b5-b984-a8871ade209d';

		const parser = new DOMParser();
		const content = parser.parseFromString(`<${wrapperTag}>${input}</${wrapperTag}>`, 'text/html');

		for (const img of content.querySelectorAll('img')) {
			let src = img.getAttribute('src');
			if (src === null) {
				continue;
			}

			const isImgixUrl = src.includes('imgix');
			const isNotCdn2 = !src.includes('cdn2');
			if (isImgixUrl || isNotCdn2) {
				continue;
			}

			const matches = src.match(/(^.*cdn2.*\/img\/)(.*)x(.*)(\/.*$)/);
			if (matches === null || !img.width || !img.height) {
				continue;
			}

			src = `${matches[1]}${img.width}x${img.height}${matches[4]}`;
			img.setAttribute('src', src);
		}

		input = content.querySelector(wrapperTag)!.innerHTML;
		content.close();

		return input;
	}

	private removeForbiddenHtml(input: string): string {
		const wrapperTag = 'a-31f16667-1148-44b5-b984-a8871ade209d';

		const parser = new DOMParser();
		const content = parser.parseFromString(`<${wrapperTag}>${input}</${wrapperTag}>`, 'text/html');

		for (const element of [...content.querySelector(wrapperTag)!.querySelectorAll('*')].reverse()) {
			this.removeForbiddenAttributes(element);

			if (this.formattingOptions.allows(element.nodeName)) {
				continue;
			}

			const parent = element.parentNode;
			if (parent === null) {
				continue;
			}

			for (const child of [...element.childNodes]) {
				parent.insertBefore(child, element);
			}

			parent.removeChild(element);
		}

		input = content.querySelector(wrapperTag)!.innerHTML;
		content.close();

		return input;
	}

	public removeForbiddenAttributes(element: Element): void {
		const whitelistedAttributes = new Map<string, readonly string[]>();

		whitelistedAttributes.set('A', ['href', 'target', 'title', 'class']);
		whitelistedAttributes.set('IMG', ['src', 'alt', 'data-align']);

		for (const attribute of [...element.attributes]) {
			if (
				whitelistedAttributes.has(element.nodeName) &&
				whitelistedAttributes.get(element.nodeName)!.includes(attribute.name)
			) {
				continue;
			}

			element.removeAttribute(attribute.name);
		}
	}
}
